import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Zap } from "lucide-react";
import logoImg from "@assets/field24_Logo_1773573795208.png";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) setSubmitted(true);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background relative overflow-hidden px-4">
      <Helmet>
        <title>Field24 — Coming Soon | South Africa's Field Service Marketplace</title>
        <meta name="description" content="Field24 is launching soon. South Africa's AI-powered marketplace for IT field service technicians. Join the waitlist." />
      </Helmet>

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center max-w-xl w-full">
        {/* Logo */}
        <img src={logoImg} alt="Field24" className="h-12 mb-10 object-contain" />

        {/* Badge */}
        <div className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
          <span className="flex h-2 w-2 rounded-full bg-primary mr-2 animate-pulse" />
          Launching Soon
        </div>

        {/* Heading */}
        <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-foreground mb-4">
          Something big is{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-cyan-500">
            coming.
          </span>
        </h1>

        <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
          South Africa's AI-first marketplace for IT field service technicians is almost ready.
          Fiber, POS, CCTV, and more — deployed in minutes.
        </p>

        {/* Waitlist form */}
        {!submitted ? (
          <form onSubmit={handleSubmit} className="w-full flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder="Enter your email for early access"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 h-12 rounded-full px-5 text-base"
            />
            <Button type="submit" size="lg" className="h-12 px-8 rounded-full text-base shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all hover:-translate-y-0.5">
              <Zap className="mr-2 h-4 w-4" /> Notify Me
            </Button>
          </form>
        ) : (
          <div className="flex items-center gap-2 text-green-500 font-medium text-base">
            <CheckCircle2 className="h-5 w-5" />
            You're on the list! We'll be in touch soon.
          </div>
        )}

        {/* Trust signals */}
        <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-green-500" /> Vetted Technicians
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-green-500" /> Fast-Pay Escrow
          </div>
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-green-500" /> AI Quality Checks
          </div>
        </div>

      </div>

      {/* Footer */}
      <p className="absolute bottom-6 text-xs text-muted-foreground">
        © {new Date().getFullYear()} Field24. All rights reserved. ·{" "}
        <a href="/privacy" className="hover:underline">Privacy</a> ·{" "}
        <a href="/terms" className="hover:underline">Terms</a>
      </p>
    </div>
  );
}
